import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export enum PhotoTypes {
  'Damage Wide-angle' = 0,
  'Damage Close-up' = 1,
  'Front Driverside' = 2,
  'Front Passenger' = 3,
  'Rear Driverside' = 4,
  'Rear Passenger' = 5,
  'Vin' = 6,
  'Odometer' = 7,
  'Lic Plate' = 8,
  'Additional Photo 1' = 9,
  'Additional Photo 2' = 10,
  'Additional Photo 3' = 11,
  'Additional Photo 4' = 12
}

export interface VehiclePhoto {
  finalUrl: string;
  type: PhotoTypes;
  uri: string;
  filename: string;
  base64: string;
  uploadStatus?: 'pending' | 'uploading' | 'completed' | 'failed';
  uploadError?: string;
}

export interface DisplayPhoto {
  uri: string;
  type: PhotoTypes;
  uploadStatus?: 'pending' | 'uploading' | 'completed' | 'failed';
}

type PartialRecord<K extends keyof any, T> = {
  [P in K]?: T;
};

interface VehiclePhotosState {
  photoMap: PartialRecord<PhotoTypes, DisplayPhoto>;
  uploadingCount: number;
  completedCount: number;
  failedCount: number;
}

const initialState: VehiclePhotosState = {
  photoMap: {},
  uploadingCount: 0,
  completedCount: 0,
  failedCount: 0
};

const vehiclePhotosSlice = createSlice({
  name: 'vehiclePhotos',
  initialState,
  reducers: {
    addPhoto: (state, action: PayloadAction<{ type: PhotoTypes; photo: DisplayPhoto }>) => {
      state.photoMap[action.payload.type] = action.payload.photo;
      state.completedCount = Object.keys(state.photoMap).length;
    },
    removePhoto: (state, action: PayloadAction<PhotoTypes>) => {
      delete state.photoMap[action.payload];
      state.completedCount = Object.keys(state.photoMap).length;
    },
    updatePhotoStatus: (
      state,
      action: PayloadAction<{
        type: PhotoTypes;
        status: 'pending' | 'uploading' | 'completed' | 'failed';
        error?: string;
      }>
    ) => {
      const photo = state.photoMap[action.payload.type];
      if (photo) {
        photo.uploadStatus = action.payload.status;
        
        if (action.payload.status === 'uploading') {
          state.uploadingCount++;
        } else if (action.payload.status === 'completed') {
          state.uploadingCount = Math.max(0, state.uploadingCount - 1);
          state.completedCount++;
        } else if (action.payload.status === 'failed') {
          state.uploadingCount = Math.max(0, state.uploadingCount - 1);
          state.failedCount++;
        }
      }
    },
    clearAllPhotos: (state) => {
      state.photoMap = {};
      state.uploadingCount = 0;
      state.completedCount = 0;
      state.failedCount = 0;
    },
    setPhotoMap: (state, action: PayloadAction<PartialRecord<PhotoTypes, DisplayPhoto>>) => {
      state.photoMap = action.payload;
      state.completedCount = Object.keys(state.photoMap).length;
    }
  }
});

export const {
  addPhoto,
  removePhoto,
  updatePhotoStatus,
  clearAllPhotos,
  setPhotoMap
} = vehiclePhotosSlice.actions;

export default vehiclePhotosSlice.reducer;

// Helper functions
export const getRequiredPhotosCount = (): number => 9;

export const getRequiredPhotoTypes = (): PhotoTypes[] => [
  PhotoTypes['Damage Wide-angle'],
  PhotoTypes['Damage Close-up'],
  PhotoTypes['Front Driverside'],
  PhotoTypes['Front Passenger'],
  PhotoTypes['Rear Driverside'],
  PhotoTypes['Rear Passenger'],
  PhotoTypes['Vin'],
  PhotoTypes['Odometer'],
  PhotoTypes['Lic Plate']
];

export const getOptionalPhotoTypes = (): PhotoTypes[] => [
  PhotoTypes['Additional Photo 1'],
  PhotoTypes['Additional Photo 2'],
  PhotoTypes['Additional Photo 3'],
  PhotoTypes['Additional Photo 4']
];

export const isRequiredPhoto = (type: PhotoTypes): boolean => {
  return getRequiredPhotoTypes().includes(type);
};

export const getCompletedRequiredPhotosCount = (
  photoMap: PartialRecord<PhotoTypes, DisplayPhoto>
): number => {
  return getRequiredPhotoTypes().filter(type => photoMap[type] !== undefined).length;
};